/**
 * Spec 10.2.1 §2.2 — ledger cockpit. Identity bar, a five-tile drill-through
 * status strip (period clock, unbanked receipts, unpresented instruments,
 * uncleared funds, pending authorisations), the accounts table (available vs
 * balance + holds/uncleared flags), recent transactions, and the exceptions
 * indicator (nil ok state / non-nil danger). All states; light+dark.
 */
import { Link, useNavigate, useParams } from "react-router";
import { useLedgerCockpit } from "../../hooks/use-ledger-workbenches";
import { money } from "../../components/ledger/format";

const PROFILE_BADGE: Record<string, string> = { trust_general: "trust general", controlled_money: "controlled money", statutory: "statutory", office: "office", custom: "custom" };
const MODE_BADGE: Record<string, string> = { out_of_band: "badge--oob", confirm: "badge--confirm", none: "badge--none" };
const MODE_LABEL: Record<string, string> = { out_of_band: "out-of-band authorisation", confirm: "confirm authorisation", none: "direct post" };
const STATUS_PILL: Record<string, string> = { posted: "pill--posted", draft: "pill--pending", reversed: "pill--reversed", cancelled: "pill--cancelled" };

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function LedgerCockpitPage() {
  const { ledgerId } = useParams<{ ledgerId: string }>();
  const navigate = useNavigate();
  const q = useLedgerCockpit(ledgerId);

  if (q.isLoading) {
    return (
      <div className="screen"><div className="surface"><div className="surface__section"><div className="status-strip">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="status-tile" style={{ cursor: "default" }}><div className="skeleton sk-line" style={{ width: "60%" }} /><div className="skeleton sk-line" style={{ height: 24, width: "45%" }} /></div>)}
      </div></div></div></div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="screen"><div className="surface"><div className="surface__body"><div className="error-state">
        <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.4}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
        <h3>Couldn’t load this ledger</h3><p>Balances and status are unavailable right now. No figures are shown rather than stale ones — retry to reload.</p>
        <button data-variant="primary" onClick={() => q.refetch()}>Retry</button>
      </div></div></div></div>
    );
  }

  const { ledger, status, accounts, recent_transactions, exceptions, pending_authorisations } = q.data;
  const sigs = (() => { try { return JSON.parse(ledger.authorised_signatories) as string[]; } catch { return []; } })();
  const period = status.open_periods[0];
  const clockDays = daysUntil(period?.due_by ?? null);
  const clockTight = clockDays != null && clockDays <= 7;
  const clockOver = clockDays != null && clockDays < 0;
  const overCount = exceptions.count;

  return (
    <div className="screen">
      <div className="row" style={{ marginBottom: 10 }}>
        <Link to="/app/trust" className="muted tiny" style={{ textDecoration: "none" }}>← Trust</Link>
      </div>
      <div className="surface">
        {/* identity bar */}
        <div className="surface__bar">
          <div className="ledger-identity">
            <span className="statutory">{ledger.name}</span>
            <span className="bank">{[ledger.bank_name, ledger.bank_bsb ? `BSB ${ledger.bank_bsb}` : null, ledger.bank_account_no ? `a/c ${ledger.bank_account_no}` : null, ledger.currency].filter(Boolean).join(" · ")}</span>
          </div>
          <span className="spacer" />
          <span className="badge badge--finance">{PROFILE_BADGE[ledger.profile] ?? ledger.profile}</span>
          <span className={`badge ${MODE_BADGE[ledger.post_authorisation] ?? "badge--neutral"}`}>{MODE_LABEL[ledger.post_authorisation] ?? ledger.post_authorisation}</span>
          {sigs.length > 0 ? <span className="muted tiny">{sigs.length} signator{sigs.length === 1 ? "y" : "ies"}</span> : null}
        </div>

        {/* status strip */}
        <div className="surface__section">
          <div className="status-strip">
            <button className={`status-tile${clockOver ? " status-tile--danger" : clockTight ? " status-tile--warn" : ""}`} onClick={() => navigate(`/app/trust/${ledgerId}/period-close`)}>
              <span className="k">Period close due</span>
              <span className="v">{clockDays == null ? "—" : `${Math.abs(clockDays)} `}<span className="tiny sec">{clockDays == null ? "" : "days"}</span></span>
              {period ? <><div className={`clock-bar${clockOver ? " is-over" : clockTight ? " is-tight" : ""}`}><span style={{ width: `${Math.max(6, Math.min(100, clockDays == null ? 50 : (1 - clockDays / 15) * 100))}%` }} /></div><span className="drill">{period.period} →</span></> : <span className="drill">No open period</span>}
            </button>
            <button className="status-tile" onClick={() => navigate(`/app/trust/${ledgerId}/period-close`)}>
              <span className="k">Unbanked receipts</span><span className="v">{status.unbanked_deposits}</span><span className="drill">Bank now →</span>
            </button>
            <button className="status-tile">
              <span className="k">Unpresented cheques</span><span className="v">{status.unpresented_instruments.length}</span>
              <span className="sub">{money(status.unpresented_instruments.reduce((s, i) => s + (i.amount_minor ?? 0), 0))} issued</span>
            </button>
            <button className="status-tile">
              <span className="k">Uncleared funds</span><span className="v money">{money(status.uncleared_total_minor)}</span>
            </button>
            <button className={`status-tile${pending_authorisations > 0 ? " status-tile--danger" : ""}`} onClick={() => navigate("/app")}>
              <span className="k">Awaiting authorisation</span><span className="v">{pending_authorisations}</span><span className="drill">Open approvals →</span>
            </button>
            <button className="status-tile" onClick={() => navigate(`/app/trust/${ledgerId}/period-history`)}>
              <span className="k">Period history</span><span className="v">↦</span><span className="drill">Closed packs & downloads →</span>
            </button>
          </div>
        </div>

        {/* accounts */}
        <div className="surface__section">
          <div className="section-head"><h3>Accounts</h3><span className="count">{accounts.length} open</span><span className="spacer" />
            {/* Spec 10.3.1 §1 — statutory print previews (open in a new tab). */}
            <Link to={`/app/print/${ledgerId}/trial_balance`} target="_blank"><button>Trial balance</button></Link>
            <Link to={`/app/print/${ledgerId}/receipts_cashbook`} target="_blank"><button>Receipts book</button></Link>
            <Link to={`/app/print/${ledgerId}/payments_cashbook`} target="_blank"><button>Payments book</button></Link>
            <Link to={`/app/trust/${ledgerId}/reconciliation`}><button>Reconciliation</button></Link>
            <Link to={`/app/trust/${ledgerId}/compose`}><button data-variant="primary">New transaction</button></Link>
          </div>
          <table className="ledger-table">
            <thead><tr><th>Account</th><th>Reference / matter</th><th>Responsible</th><th className="num">Available</th><th className="num">Balance</th><th>Flags</th></tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/app/trust/${ledgerId}/accounts/${a.id}`)}>
                  <td>{a.account_no} · {a.title_name}</td>
                  <td className="muted">{a.reference ?? (a.kind === "bank" ? "control account" : "—")}</td>
                  <td className="muted">{a.responsible_user ?? "—"}</td>
                  <td className="num money">{money(a.available_minor)}</td>
                  <td className="num money">{money(a.balance_minor)}</td>
                  <td>
                    {a.held_minor > 0 ? <span className="pill pill--hold"><span className="dot" />hold {money(a.held_minor)}</span> : null}
                    {a.uncleared_minor > 0 ? <span className="pill pill--issued"><span className="dot" />uncleared {money(a.uncleared_minor)}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* recent transactions + exceptions */}
        <div className="surface__section">
          <div className="row" style={{ alignItems: "stretch", gap: 16 }}>
            <div style={{ flex: 2 }}>
              <div className="section-head"><h3>Recent transactions</h3></div>
              <table className="ledger-table">
                <thead><tr><th>#</th><th>Type</th><th>Particulars</th><th className="num">Amount</th><th>Status</th></tr></thead>
                <tbody>
                  {recent_transactions.length === 0 ? (
                    <tr><td colSpan={5} className="muted tiny">No transactions yet.</td></tr>
                  ) : recent_transactions.map((t) => (
                    <tr key={t.id}>
                      <td className="row-doc">{t.doc_no ?? (t.txn_no ? `TX-${t.txn_no}` : "—")}</td>
                      <td style={{ textTransform: "capitalize" }}>{t.type}</td>
                      <td>{t.reason ?? t.counterparty_name ?? "—"}</td>
                      <td className="num money">{money(t.amount_minor)}</td>
                      <td><span className={`pill ${STATUS_PILL[t.status] ?? "pill--draft"}`}><span className="dot" />{t.status === "draft" ? "awaiting auth" : t.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ flex: 1 }}>
              <div className="section-head"><h3>Exceptions</h3>{overCount > 0 ? <span className="count">{overCount} this period</span> : null}</div>
              {overCount === 0 ? (
                <div className="guard guard--ok"><svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M20 6 9 17l-5-5" /></svg><div>No overdrawn accounts this period. The contemporaneous exception report is empty.</div></div>
              ) : (
                <div className="guard guard--blocked"><svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><div>{overCount} account{overCount === 1 ? " was" : "s were"} overdrawn during the period. The period cannot close until every exception is acknowledged.</div></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
